import { Component, OnInit, Input, SimpleChanges, Output, EventEmitter, forwardRef } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { 
    IonGrid, IonRow, IonCol, IonThumbnail
} from '@ionic/angular/standalone';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { 
    MarvInputComponent, MarvSelectComponent, MarvSelectItem, MarvInputDateComponent, MarvTextareaComponent, 
    MarvInputFileComponent, MarvInputFile, MarvFieldComponent, MarvButtonComponent
} from '@marv/components';
import { StoreCheckInputModel, SCIMField, StoreCheckInputRuleDefinition, StoreCheckObjectiveValueDto, RuleOperator } from '@app/models';

export class ValidateQuestionnaireEvent {
    status: boolean;
    values?: Array<StoreCheckObjectiveValueDto>;
}

export class StoreCheckSalesInputRule {
    // name of field which trigg this rule
    trigger!: string;
    // expected valuy by the field to trigg this rule
    value: any;

    operator!: RuleOperator;
    // mandatories fields for this rules
    mandatory: string[] = [];
    // forbidden fields for this rules
    forbidden: string[] = [];
    // optional fields for this rules
    optional: string[] = [];

    showOnDashboard!: boolean;
    message!: string;

}

@Component({
    selector: 'app-questionnaire',
    templateUrl: './questionnaire.component.html',
    styleUrls: ['./questionnaire.component.scss'],
    imports: [
        IonGrid, IonRow, IonCol, IonThumbnail, AsyncPipe, TranslatePipe, ReactiveFormsModule,
        forwardRef(() => MarvFieldComponent), forwardRef(() => MarvButtonComponent), forwardRef(() => MarvInputComponent), 
        forwardRef(() => MarvSelectComponent), forwardRef(() => MarvInputDateComponent), forwardRef(() => MarvTextareaComponent), 
        forwardRef(() => MarvInputFileComponent)
    ]
})
export class QuestionnaireComponent  implements OnInit {

    @Input({alias : 'model', required : true}) model: StoreCheckInputModel;   
    @Input({alias : 'values', required : true}) values: Map<string, any> = new Map();
    @Input({alias : 'title', required : false}) title: string;   
    @Input({alias : 'disabled', required : false}) disabledForm: boolean = false;   
    @Output() onValidateQuestionnaire = new EventEmitter<ValidateQuestionnaireEvent>();
    
    isFetchingValidation: boolean = false;
    questionnaireForm: FormGroup = new FormGroup({});
    fieldsSubscription: Map<string, Subscription> = new Map<string, Subscription>;
    fieldsMap: Map<string, SCIMField> = new Map<string, SCIMField>;
    fieldsPhotos: {[key: string]: MarvInputFile} = {};
    fieldsVisibility: Map<string, boolean> = new Map<string, boolean>;    
    rules: Array<StoreCheckSalesInputRule> = [];
    mapOptions: Map<string, MarvSelectItem[]> = new Map<string, MarvSelectItem[]>;
    booleanOptions: Array<MarvSelectItem> = [
        new MarvSelectItem(this.translateService.instant('YES'), 'true'),
        new MarvSelectItem(this.translateService.instant('NO'), 'false')
    ];
    photosFileExt: Array<string> = ['png','jpeg', 'jpg'];
    
    constructor(
        public translateService: TranslateService
    ) { }

    ngOnInit() {
        
    }
    
    async ngOnChanges(changes: SimpleChanges) {
        if(changes['model'].currentValue){           
         
            this.questionnaireForm = new FormGroup({});             
            this.rules = [];    
            this.fieldsMap.clear();
            this.fieldsPhotos = {};
            this.mapOptions.clear();
             
            for (let r of this.model.rules) {
                if (r.scope === 'SC') {
                    this.rules = this.rules.concat(this.extractScDataInputRuleFromDefinition(r));
                }
            }
            
            for(let group of this.model.scDataGroups){                
                for(let field of group.scdatafields){                      

                    this.fieldsMap.set(field.code, field);
                    this.fieldsPhotos[field.code] = new MarvInputFile();
                    if(['ENUM', 'MCQ'].indexOf(field.type) !== -1){
                        let items: Array<MarvSelectItem> = this.parseOptions(field);                        
                        this.mapOptions.set(field.code, items);
                    }
                    
                    let validators: Array<any> = [];
                    if(field.mandatory){
                        validators.push(Validators.required);
                    }
                    this.questionnaireForm.addControl(field.code, new FormControl(null, validators), {emitEvent: false}); 
                    field.defaultValue
                    this.fieldsVisibility.set(field.code, true);
                    
                    this.fieldsSubscription.set(field.code,
                        this.questionnaireForm.controls[field.code].valueChanges.subscribe(value => {                      
                            this.onUpdateFieldValue(field.code);
                        })
                    );
                    
                }                
            }
            
            this.fieldsMap.forEach(field => {
                this.questionnaireForm.controls[field.code].setValue(this.parseFieldValue(field, this.values.get(field.code)), {emitEvent: false});
                this.onUpdateFieldValue(field.code);
            });            
        } 
           
    }
    
    parseOptions(model: SCIMField) {
        let options: Array<MarvSelectItem> = [];
        let fmt = model.dataFormat;

        if (fmt != null) {
            for (let s of fmt.split("$")) {
                let l = s.split("=");
                if (l.length < 2){
                    continue;
                }
                // check if option must be used
                if (l[0].startsWith("!")) {
                    continue;
                }

                options.push(new MarvSelectItem(l[1], parseInt(l[0])));

            }
        }
        return options;
    }
    
    shouldUseTextArea(field: SCIMField): boolean {
        return true;
    }
    
    /*    
    shouldUseTextArea(field: SCIMField): boolean {
        if (field == null || field.dataFormat == null) return false;
        if (field.dataFormat.toLowerCase().indexOf("multibarcode") >= 0) return true;
        if (field.dataFormat.toLowerCase().indexOf("multiline") >= 0) return true;
        return false;
    }
    */
    
    onUpdateFieldValue(codeField: string){
       
        let field = this.fieldsMap.get(codeField);
       
        if(field){
       
            let v = this.formatFieldValue(field, this.questionnaireForm.controls[field.code].value);                   
                    
            let matchingRules = this.rules.filter(r => r.trigger == field.fqCode);
            matchingRules = matchingRules.filter(r => r.operator.matches(r.value, v));

            for (let r of matchingRules) {

                for(let fieldCode of r.forbidden){
                    let f = this.fieldsMap.get(fieldCode);
                    if(f){
                        this.fieldsVisibility.set(fieldCode, false)
                        this.questionnaireForm.controls[fieldCode].setValue(null);
                        this.questionnaireForm.controls[fieldCode].removeValidators(Validators.required);
                    }
                }
                for(let fieldCode of r.optional){
                    let f = this.fieldsMap.get(fieldCode);
                    if(f){
                        this.fieldsVisibility.set(fieldCode, true);
                        if(f.mandatory){
                            this.questionnaireForm.controls[fieldCode].addValidators(Validators.required);
                        }
                        else{
                            this.questionnaireForm.controls[fieldCode].removeValidators(Validators.required);
                        }
                        
                        if(f.defaultValue && !this.questionnaireForm.controls[fieldCode].value){
                            this.questionnaireForm.controls[fieldCode].setValue(f.defaultValue);
                        }
                    }
                }
                for(let fieldCode of r.mandatory){
                    let f = this.fieldsMap.get(fieldCode);
                    if(f){
                        this.fieldsVisibility.set(fieldCode, true);
                        if(f.mandatory){
                            this.questionnaireForm.controls[fieldCode].addValidators(Validators.required);
                        }
                        else{
                            this.questionnaireForm.controls[fieldCode].removeValidators(Validators.required);
                        }
                        
                        if(f.defaultValue && !this.questionnaireForm.controls[fieldCode].value){
                            this.questionnaireForm.controls[fieldCode].setValue(f.defaultValue);
                        }
                    }
                }

            }
        }
    }
    
    extractScDataInputRuleFromDefinition(ruleToExtract: StoreCheckInputRuleDefinition): Array<StoreCheckSalesInputRule> {
        let rules: Array<StoreCheckSalesInputRule> = [];
        if (ruleToExtract.triggerVariables != null){
            for (let trigger of ruleToExtract.triggerVariables) {
                var iRule = new StoreCheckSalesInputRule();
                iRule.trigger = trigger;
                var tvars = ruleToExtract.templateVariables.split("\n");
                for (let l of tvars) {
                    var ll = l.split("=");
                    if (ll[0] == 'operator') {
                        iRule.operator = new RuleOperator(ll[1]);
                    } else if (ll[0] == 'value') {
                        iRule.value = ll[1];
                    } else if (ll[0] == 'mandatory') {
                        iRule.mandatory = ll[1].split('|').map(s => s);
                    } else if (ll[0] == 'forbidden') {
                        iRule.forbidden = ll[1].split('|').map(s => s);
                    } else if (ll[0] == 'optional') {
                        iRule.optional = ll[1].split('|').map(s => s);
                    }
                }
                rules.push(iRule);
            }
        }
        return rules;
    }
    
    parseFieldValue(field: SCIMField, value: any): any{
        if(value && value !== ''){
            switch(field.type){
                case 'MCQ': 
                    value = value.split('|').map((t: string) => parseInt(t));
                    break;
                case 'PHOTO': 
                    this.fieldsPhotos[field.code].url = value;
                    value = null;
                    break;
            }
        }
        else{
            value = null;
        }
        return value;
    }
    
    async formatFieldValue(field: SCIMField, value: any): Promise<string> {
        
        if(value === null){
            value = '';
        }
        
        switch(field.type){
            case 'MCQ': 
                if(Array.isArray(value)){
                    value = value.join('|');
                }
                break;
            case 'PHOTO':                     
                value = this.values.get(field.code);
                if(this.fieldsPhotos[field.code] !== null){
                    if(this.fieldsPhotos[field.code].base64 !== null){      
                        value = this.fieldsPhotos[field.code].base64?.split(',')[1];
                    }
                }
                else if(value && value !== ''){
                    value = '0';
                }             
                break;
            default:
                value = value.toString();
                break;
        }
        
        return value;
    }
    
    async saveQuestionnaire() {
        if(!this.disabledForm){            
            if(this.questionnaireForm.valid){          
            
                this.isFetchingValidation = true;
                let values: Array<StoreCheckObjectiveValueDto> = [];  
                for(let field of Array.from(this.fieldsMap.values())){
                    let v = await this.formatFieldValue(field, this.questionnaireForm.controls[field.code].value);                   
                    values.push({
                        key: field.code,
                        value: v
                    });
                }
                     
                this.onValidateQuestionnaire.emit({status: true, values: values});
            }
            else{
                for (var controlName in this.questionnaireForm.controls) {
                    if(!this.questionnaireForm.controls[controlName].valid) {
                        this.questionnaireForm.controls[controlName].markAsTouched();
                    }
                }
            }            
        }  
    }
    
    close() {
        this.onValidateQuestionnaire.emit({status: false});
    }
}
